import PropTypes from 'prop-types'
import {
  Children,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { noop } from 'utils'
import classNames from 'classnames'
import useFocusOnError from 'utils/hooks/useFocusOnError'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'
import NoneAnchor from '../NoneAnchor'
import useOutsideClick from '../../utils/hooks/useOutsideClick'

const Options = ({ options, selectValue, selectedItem }) =>
  options.map((item, seq) => {
    const optionalProps = item.props.disabled
      ? {}
      : {
          onClick: () => {
            item.props.onClick && item.props.onClick()
            selectValue(
              item.props.value === undefined
                ? item.props.children
                : item.props.value
            )
          },
        }

    const on =
      item?.props?.value && selectedItem?.props?.value
        ? item?.props?.value === selectedItem?.props?.value
        : item?.props?.text
        ? item?.props?.text === selectedItem
        : item === selectedItem
    return (
      <li
        key={seq}
        className={classNames({
          on,
          in_active: item.props.disabled,
        })}
        {...optionalProps}>
        <NoneAnchor
          tabIndex={-1}
          className="link_option"
          data-tiara-action-name={item.props.tiaraAction}>
          {item}
        </NoneAnchor>
      </li>
    )
  })

const SelectBox = ({
  type = 'basic',
  value,
  disabled,
  placeholder,
  error,
  focusOnError,
  style,
  className,
  subTitle,
  noBlurOnDefault,
  onValueChange = noop,
  onClick = noop,
  children,
  footer,
}) => {
  const scrollRef = useRef()
  const focusRef = useRef()
  const onValueChangeRef = useUpdatingRef(onValueChange)
  const valueRef = useUpdatingRef(value)

  const [isOpened, setIsOpened] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isDefaultValueSelected, setIsDefaultValueSelected] = useState(true)

  const [selectedValue, setSelectedValue] = useState(() => {
    if (value !== undefined) return value
    const defaultItem = Children.toArray(children).find(
      item => item.props.defaultSelected
    )
    return defaultItem
      ? defaultItem.props.value || defaultItem.props.children
      : null
  })

  const selectedItem = useMemo(() => {
    const selectedChild = Children.toArray(children).find(item => {
      const compareValue = value === undefined ? selectedValue : value
      return (
        compareValue ===
        (item.props.value === undefined
          ? item.props.children
          : item.props.value)
      )
    })
    return selectedChild && selectedChild.props.text
      ? selectedChild.props.text
      : selectedChild
  }, [value, selectedValue, children])

  const getSelectedIndex = useCallback(
    () =>
      Children.toArray(children).findIndex(
        item => selectedValue === (item.props.value || item.props.children)
      ),
    [children, selectedValue]
  )

  const selectValue = useCallback(
    itemValue => {
      setIsDefaultValueSelected(false)
      if (selectedValue !== itemValue) setSelectedValue(itemValue)
      if (valueRef.current !== itemValue) onValueChangeRef.current(itemValue)
    },
    [selectedValue, valueRef, onValueChangeRef]
  )

  useEffect(() => {
    if (value !== undefined && value !== selectedValue) {
      setSelectedValue(value)
    }
  }, [selectedValue, onValueChangeRef, value])

  useLayoutEffect(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight } = scrollRef.current
      const listHeight = scrollRef.current.clientHeight
      const scrollBottom = scrollTop + listHeight
      const eachItemHeight = scrollHeight / children.length
      const currentItemScrollTop = eachItemHeight * getSelectedIndex()
      const currentItemScrollBottom = currentItemScrollTop + eachItemHeight
      if (currentItemScrollTop < scrollTop) {
        scrollRef.current.scrollTop = currentItemScrollTop
      } else if (currentItemScrollBottom > scrollBottom) {
        scrollRef.current.scrollTop = currentItemScrollBottom - listHeight
      }
    }
  }, [selectedItem, children, getSelectedIndex])

  const getNthValue = useCallback(
    index => children[index].props.value || children[index].props.children,
    [children]
  )

  const onKeyDown = useCallback(
    e => {
      let selectedIndex
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault()
          setIsOpened(current => !current)
          break

        case 'ArrowDown':
          e.preventDefault()
          selectedIndex = getSelectedIndex()
          selectValue(
            getNthValue((selectedIndex + 1 + children.length) % children.length)
          )
          setIsOpened(true)
          break

        case 'ArrowUp':
          e.preventDefault()
          selectedIndex = getSelectedIndex()
          selectValue(
            getNthValue((selectedIndex - 1 + children.length) % children.length)
          )
          setIsOpened(true)
          break

        case 'Tab':
          setIsOpened(false)
          break
        default:
      }
    },
    [children, getNthValue, getSelectedIndex, selectValue]
  )

  useFocusOnError(focusRef, error, focusOnError)

  const options = Children.toArray(children).filter(
    node => node && node.type === SelectBox.Item
  )
  const ref = useOutsideClick(() => setIsOpened(false))
  return (
    <div
      ref={ref}
      onClick={() => setIsOpened(current => !current)}
      onKeyDown={subTitle ? noop : onKeyDown}
      className={classNames('opt_select', className, {
        on: isFocused,
        opt_open: isOpened,
        in_active: disabled,
        in_error: error,
      })}
      style={style}>
      <div className="screen_out">도메인 선택상자</div>
      <span className="screen_out">선택내용 : </span>
      <NoneAnchor
        ref={focusRef}
        className={classNames('link_selected', {
          link_default: (!value || isDefaultValueSelected) && !noBlurOnDefault,
        })}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={onClick}>
        {selectedItem || placeholder}
      </NoneAnchor>
      <span className="ico_arr" />
      <div className="screen_out">선택옵션</div>
      {options && options.length > 0 && (
        <div className="opt_list" style={isOpened ? {} : { display: 'none' }}>
          {subTitle ? (
            <>
              <span className="subtit_opt">{subTitle}</span>
              <div className="sublist_opt">
                <ul className="list_opt" ref={scrollRef}>
                  <Options
                    options={options}
                    selectedItem={selectedItem}
                    selectValue={selectValue}
                  />
                </ul>
              </div>
            </>
          ) : type === 'dashboard' ? (
            <div className="list_opt2">
              <ul className="list_opt sub_listopt" ref={scrollRef}>
                <Options
                  options={options}
                  selectedItem={selectedItem}
                  selectValue={selectValue}
                />
              </ul>
            </div>
          ) : (
            <ul className="list_opt" ref={scrollRef}>
              <Options
                options={options}
                selectedItem={selectedItem}
                selectValue={selectValue}
              />
            </ul>
          )}
          {footer && (
            <div
              className="util_btn"
              onClick={event => event.stopPropagation()}>
              {footer}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

SelectBox.propTypes = {
  children: PropTypes.any,
  footer: PropTypes.node,
  disabled: PropTypes.bool,
  error: PropTypes.any,
  focusOnError: PropTypes.bool,
  noBlurOnDefault: PropTypes.bool,
  onValueChange: PropTypes.func,
  placeholder: PropTypes.node,
  style: PropTypes.object,
  value: PropTypes.any,
  className: PropTypes.string,
  subTitle: PropTypes.node,
  selectedItemView: PropTypes.func,
  onClick: PropTypes.func,
  type: PropTypes.PropTypes.oneOf(['basic', 'dashboard']),
}

// noinspection JSUnusedLocalSymbols
const SelectBoxItem = ({
  defaultSelected,
  value,
  text,
  children,
  disabled,
  tiaraAction,
  onClick = noop,
}) => children || null
SelectBoxItem.displayName = 'SelectBox.Item'
SelectBoxItem.propTypes = {
  defaultSelected: PropTypes.bool,
  value: PropTypes.any,
  text: PropTypes.node,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  tiaraAction: PropTypes.string,
  onClick: PropTypes.func,
}
SelectBox.Item = SelectBoxItem

export default SelectBox
